import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type LeaveYearRecord = {
  id: ID;
  label: string;
  startDate: string;
  endDate: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

const LS_KEY = "inventory.leaveYears.v1";

type ApiRow = {
  id?: number | string;
  label?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
};

function load(): LeaveYearRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LeaveYearRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function save(items: LeaveYearRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(items));
}

function extractRows(body: unknown): ApiRow[] {
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (Array.isArray(data)) return data as ApiRow[];
  return [];
}

function mapRow(r: ApiRow): LeaveYearRecord {
  return {
    id: String(r.id ?? ""),
    label: String(r.label ?? ""),
    startDate: String(r.start_date ?? "").slice(0, 10),
    endDate: String(r.end_date ?? "").slice(0, 10),
    active: Boolean(r.active),
    createdAt: String(r.created_at ?? new Date().toISOString()),
    updatedAt: String(r.updated_at ?? r.created_at ?? new Date().toISOString()),
  };
}

export function leaveYearErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

export function listLeaveYears(): LeaveYearRecord[] {
  return load().sort((a, b) => b.startDate.localeCompare(a.startDate) || String(b.id).localeCompare(String(a.id)));
}

export function getActiveLeaveYear(): LeaveYearRecord | undefined {
  return load().find((y) => y.active);
}

export async function syncLeaveYearsFromApi(): Promise<LeaveYearRecord[]> {
  const response = await api.get("/api/v1/leave-years", { params: { page: 1, per_page: 200 } });
  const mapped = extractRows(response.data).map(mapRow).filter((x) => Boolean(x.id));
  save(mapped);
  return listLeaveYears();
}

export async function createLeaveYear(data: {
  label: string;
  startDate: string;
  endDate: string;
  active?: boolean;
  importTemplateHolidays?: boolean;
}): Promise<{ record: LeaveYearRecord; message: string }> {
  const payload: Record<string, unknown> = {
    label: data.label.trim(),
    start_date: data.startDate,
    end_date: data.endDate,
    active: Boolean(data.active),
  };
  if (data.importTemplateHolidays === true) {
    payload.import_template_holidays = true;
  }
  const response = await api.post<{ message?: string; data?: ApiRow }>("/api/v1/leave-years", payload);
  const rec = response.data?.data;
  if (!rec?.id) throw new Error("Invalid create leave year response.");
  const record = mapRow(rec);
  const next = listLeaveYears().filter((x) => x.id !== record.id);
  next.unshift(record);
  // if activated, deactivate others locally too
  if (record.active) {
    for (const r of next) {
      if (r.id !== record.id) r.active = false;
    }
  }
  save(next);
  return { record, message: response.data?.message || "Leave year created." };
}

export async function updateLeaveYear(
  id: ID,
  data: Partial<{ label: string; startDate: string; endDate: string; active: boolean; importTemplateHolidays: boolean }>,
): Promise<{ record: LeaveYearRecord; message: string } | null> {
  const items = listLeaveYears();
  const idx = items.findIndex((x) => x.id === id);
  if (idx === -1) return null;
  const payload: Record<string, unknown> = {};
  if (data.label !== undefined) payload.label = data.label.trim();
  if (data.startDate !== undefined) payload.start_date = data.startDate;
  if (data.endDate !== undefined) payload.end_date = data.endDate;
  if (data.active !== undefined) payload.active = Boolean(data.active);
  if (data.importTemplateHolidays === true) {
    payload.import_template_holidays = true;
  }
  const response = await api.put<{ message?: string; data?: ApiRow }>(`/api/v1/leave-years/${id}`, payload);
  const rec = response.data?.data;
  if (!rec?.id) throw new Error("Invalid update leave year response.");
  const record = mapRow(rec);
  items[idx] = record;
  if (record.active) {
    for (const r of items) {
      if (r.id !== record.id) r.active = false;
    }
  }
  save(items);
  return { record, message: response.data?.message || "Leave year updated." };
}

export async function activateLeaveYear(id: ID): Promise<{ record: LeaveYearRecord; message: string }> {
  const response = await api.post<{ message?: string; data?: ApiRow }>(`/api/v1/leave-years/${id}/activate`);
  const rec = response.data?.data;
  if (!rec?.id) throw new Error("Invalid activate response.");
  const record = mapRow(rec);
  const items = listLeaveYears();
  const next = items.map((y) => ({ ...y, active: y.id === record.id }));
  // ensure record fields up-to-date
  const i = next.findIndex((y) => y.id === record.id);
  if (i >= 0) next[i] = { ...record, active: true };
  else next.unshift({ ...record, active: true });
  save(next);
  return { record: { ...record, active: true }, message: response.data?.message || "Activated." };
}

export async function deleteLeaveYear(id: ID): Promise<{ success: boolean; message: string }> {
  const response = await api.delete<{ message?: string }>(`/api/v1/leave-years/${id}`);
  const items = listLeaveYears();
  const next = items.filter((y) => y.id !== id);
  save(next);
  return { success: next.length !== items.length, message: response.data?.message || "Deleted." };
}

